import { useState, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { KnowledgeUnit, PassId } from '../types'

export function useKUs() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchKUs = useCallback(async (standardId: string, passId?: PassId): Promise<KnowledgeUnit[]> => {
    setLoading(true)
    setError(null)
    let query = supabase
      .from('knowledge_units')
      .select('*, votes(*), run:runs(*)')
      .eq('standard_id', standardId)
      .order('created_at', { ascending: true })

    if (passId) query = query.eq('pass_id', passId)

    const { data, error: err } = await query
    setLoading(false)
    if (err) {
      setError(err.message)
      return []
    }
    return (data as KnowledgeUnit[]) || []
  }, [])

  const updateKU = useCallback(async (id: string, patch: Partial<KnowledgeUnit>): Promise<KnowledgeUnit | null> => {
    const { data, error: err } = await supabase
      .from('knowledge_units')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    if (err) {
      setError(err.message)
      return null
    }
    return data as KnowledgeUnit
  }, [])

  const upsertVote = useCallback(async (kuId: string, userId: string, score: number, comment: string) => {
    const { error: err } = await supabase
      .from('votes')
      .upsert(
        { ku_id: kuId, user_id: userId, score, comment, updated_at: new Date().toISOString() },
        { onConflict: 'ku_id,user_id' },
      )
    if (err) setError(err.message)
  }, [])

  const fetchStandardsWithKUs = useCallback(async (): Promise<Set<string>> => {
    const { data } = await supabase.from('knowledge_units').select('standard_id')
    if (!data) return new Set()
    return new Set((data as { standard_id: string }[]).map((r) => r.standard_id))
  }, [])

  return { fetchKUs, updateKU, upsertVote, fetchStandardsWithKUs, loading, error }
}
